import {
  array,
  combineLatestTyped,
  promiseState,
  useDataStream,
  useNavigator,
  usePathNameList,
  useStreamValue
} from 'annotation-tools/utils';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { map, switchMap } from 'rxjs/operators';
import { viewTexts } from '../../../constants/view-texts';
import { getListItem } from '../../../global-state/database/common/get-list-item';
import { isItemNotFoundError } from '../../../global-state/database/common/is-item-not-found-error';
import {
  ListItemWithPromiseState,
  listItemWithPromiseStateInit
} from '../../../global-state/database/common/list-item-with-status';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../../global-state/database/common/list-with-status';
import { companyListOf } from '../../../global-state/database/company-list';
import { modelImageListOf } from '../../../global-state/database/model-image-list';
import { labelListOf } from '../../../global-state/database/model-label-list';
import { modelListOf } from '../../../global-state/database/model-list';
import {
  companyId$,
  koumutenId$,
  modelId$
} from '../../../global-state/router-id';
import { useLoadingPage } from '../../../global-state/show-loading-page';
import { pathnameElements, routes } from '../../../routing/routes';
import { ICompanyType } from '../../../types/record/company';
import { IImageInfoType } from '../../../types/record/image-info';
import { ILabelFromApiType } from '../../../types/record/label';
import { IModelType } from '../../../types/record/model';
import { NotFoundTemplate } from '../../shared/not-found-page-template';
import { ModelRootView } from './model-root-view';

const pathNameLastToIndex = (pathNameLast: string): number | undefined => {
  switch (pathNameLast) {
    case pathnameElements.summary:
      return 0;
    case pathnameElements.labels:
      return 1;
    case pathnameElements.images:
      return 2;
    default:
      return undefined;
  }
};

export const ModelRoot: FC<{}> = memo(() => {
  const pathName = usePathNameList();
  const pathNameLast = useMemo(() => array.last(pathName) ?? '', [pathName]);

  const companyList$ = useDataStream<ListWithPromiseState<ICompanyType>>(
    listWithPromiseStateInit<ICompanyType>(),
    koumutenId$.pipe(switchMap(companyListOf))
  );

  const company$ = useDataStream<ListItemWithPromiseState<ICompanyType>>(
    listItemWithPromiseStateInit<ICompanyType>(),
    combineLatestTyped(companyList$, companyId$).pipe(
      map(([list, id]) => getListItem(list, id))
    )
  );

  const modelList$ = useDataStream<ListWithPromiseState<IModelType>>(
    listWithPromiseStateInit<IModelType>(),
    companyId$.pipe(switchMap(modelListOf))
  );

  const model$ = useDataStream<ListItemWithPromiseState<IModelType>>(
    listItemWithPromiseStateInit(),
    combineLatestTyped(modelList$, modelId$).pipe(
      map(([list, id]) => getListItem(list, id))
    )
  );

  const labelList$ = useDataStream<ListWithPromiseState<ILabelFromApiType>>(
    listWithPromiseStateInit<ILabelFromApiType>(),
    modelId$.pipe(switchMap(labelListOf))
  );

  const imageList$ = useDataStream<ListWithPromiseState<IImageInfoType>>(
    listWithPromiseStateInit<IImageInfoType>(),
    modelId$.pipe(switchMap(modelImageListOf))
  );

  /* extracted values */

  const company = useStreamValue(company$);
  const model = useStreamValue(model$);
  const koumutenId = useStreamValue(koumutenId$);
  const companyId = useStreamValue(companyId$);
  const modelId = useStreamValue(modelId$);
  const labelList = useStreamValue(labelList$);
  const imageList = useStreamValue(imageList$);

  // pathnameと対応させるため、routerを使ってタブ切り替えを制御
  const tabIndex = useMemo(
    // dummy comment to control prettier
    () => pathNameLastToIndex(pathNameLast) ?? 0,
    [pathNameLast]
  );

  /* handlers */

  const navigator = useNavigator();

  const tabIndexChange = useCallback(
    (idx: number) => {
      if (
        koumutenId !== undefined &&
        companyId !== undefined &&
        modelId !== undefined
      ) {
        switch (idx) {
          case 0:
            navigator(routes.modelRoot(koumutenId, companyId, modelId));
            break;
          case 1:
            navigator(routes.labelListOfModel(koumutenId, companyId, modelId));
            break;
          case 2:
            navigator(routes.modelImageList(koumutenId, companyId, modelId));
            break;
          default:
            break;
        }
      }
    },
    [navigator, koumutenId, companyId, modelId]
  );

  const onEditLabelClick = useCallback(() => {
    tabIndexChange(1);
  }, [tabIndexChange]);

  const onEditModelImagesClick = useCallback(() => {
    tabIndexChange(2);
  }, [tabIndexChange]);

  useLoadingPage(
    koumutenId === undefined ||
      company?.status === 'pending' ||
      model?.status === 'pending' ||
      labelList?.status === 'pending' ||
      imageList?.status === 'pending'
  );

  return (
    <>
      {koumutenId === undefined ||
      company?.value.latest === undefined ||
      model?.value.latest === undefined ||
      labelList === undefined ||
      imageList === undefined ? (
        undefined
      ) : labelList.status === promiseState.error ? (
        // TODO: エラー表示を整備する
        <NotFoundTemplate message={viewTexts.notFoundMessages.labelList} />
      ) : imageList.status === promiseState.error ? (
        <NotFoundTemplate message={viewTexts.notFoundMessages.modelImageList} />
      ) : isItemNotFoundError(company) ? (
        <NotFoundTemplate message={viewTexts.notFoundMessages.company} />
      ) : isItemNotFoundError(model) ? (
        <NotFoundTemplate message={viewTexts.notFoundMessages.model} />
      ) : (
        <ModelRootView
          tabIndex={tabIndex}
          tabIndexChange={tabIndexChange}
          koumutenId={koumutenId}
          company={company.value.latest}
          model={model.value.latest}
          labelList={labelList.value.latest}
          imageList={imageList.value.latest}
          onEditLabelClick={onEditLabelClick}
          onEditModelImagesClick={onEditModelImagesClick}
        />
      )}
    </>
  );
});

ModelRoot.displayName = 'ModelRoot';
